/*
 * Judge.me reviews on a landing page, in our own order, with every Judge.me feature kept
 * (Verified by Shop / Verified buyer badges, photo popup, read more, helpful votes, "Write a review").
 *
 * The section renders Judge.me's own widget from the product metafield ({{ product.metafields.judgeme.widget }}),
 * so Judge.me loads all of its widget code. This script then:
 *   - replaces the widget's review list with reviews fetched from Judge.me's public widget endpoint (the same one its
 *     own pager calls), one star level at a time, photos first inside each level: 5★ photos, 5★ text, 4★ photos ...;
 *   - optionally keeps only some products (config.keep: regex on the product URL) and, right after the kept 5★ photo
 *     reviews, shows 5★ photo reviews of the other products (config.extras), each labelled with its product;
 *   - adds the product name under the reviewer (config.showProduct);
 *   - swaps Judge.me's pager for one "Load more reviews" button. Nothing is hidden by rating: every kept review can be
 *     reached by loading more (FTC Consumer Review Rule).
 * Judge.me's store-wide settings are not touched. If a fetch fails, Judge.me's own widget is left as it was.
 *
 * Config: a JSON script tag with the data-jmr-config attribute inside the mount element, see README.
 */
(function () {
  'use strict';
  var mount = document.querySelector('[data-jmr]');
  if (!mount) return;
  var C = {};
  try { C = JSON.parse((mount.querySelector('[data-jmr-config]') || {}).textContent || '{}'); } catch (e) {}
  var STEP = C.perPage || 5;                         // cards added per click
  var FETCH = Math.min(C.fetchSize || 30, 30);       // Judge.me caps a page at 30
  var LEVELS = C.levels || [5, 4, 3, 2, 1];
  var KEEP = C.keep ? new RegExp(C.keep) : null;     // tested on the review's product URL path
  var EXTRAS = !!(KEEP && C.extras);
  var LABEL = C.moreLabel || 'Load more reviews';
  var ENDPOINTS = ['https://cdn.judge.me/reviews/reviews_for_widget', 'https://api.judge.me/reviews/reviews_for_widget'];

  var widget, list, btn;
  var queue = [], seen = {}, done = false, busy = false;
  // stream position: star level, Judge.me page inside it, and whether that level's photo reviews are over
  var at = {level: 0, page: 0, total: null, photosOver: false}, stash = [];

  function hasPhoto(rev) { return !!rev.querySelector('.jdgm-rev__pic-link, .jdgm-rev__pics img, .jdgm-rev__vids video, [data-video-url]'); }
  function kept(rev) { return !KEEP || KEEP.test((rev.getAttribute('data-product-url') || '').split('?')[0]); }
  function productName(rev) {
    var t = (rev.getAttribute('data-product-title') || '').replace(/["“”]/g, '').trim();
    // "Limitless - Philippians 4:13 Wallet" -> "Limitless Wallet" (drops a " - Book 1:2" verse)
    return C.stripVerse === false ? t : t.replace(/\s+-\s+(?:\d\s)?[A-Z][a-z]+\.?\s\d+:\d+(?:-\d+)?/, '').replace(/\s{2,}/g, ' ');
  }

  function url(level, page, endpoint) {
    var p = {url: window.Shopify && Shopify.shop, shop_domain: window.Shopify && Shopify.shop, platform: 'shopify'};
    p.product_id = widget.getAttribute('data-id');
    p.page = page; p.per_page = FETCH; p.sort_by = 'pictures_first'; p.filter_rating = LEVELS[level];
    return endpoint + '?' + Object.keys(p).map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(p[k]); }).join('&');
  }
  // Judge.me's CDN now and then answers without CORS headers; its origin API is the retry, and once the CDN has
  // failed the next pages go straight to the API (ep remembers the endpoint that worked)
  var ep = 0;
  function getJson(level, page, i) {
    i = i || ep;
    return fetch(url(level, page, ENDPOINTS[i])).then(function (r) { if (!r.ok) throw new Error('judge.me ' + r.status); ep = i; return r.json(); })
      .catch(function (e) {
        if (i + 1 >= ENDPOINTS.length) throw e;
        return new Promise(function (res) { setTimeout(res, 600); }).then(function () { return getJson(level, page, i + 1); });
      });
  }

  // Pull the next Judge.me page and push the cards that belong on the page, in order, into the queue.
  function pull() {
    if (done) return Promise.resolve();
    if (at.total != null && at.page * FETCH >= at.total) {          // this level is finished
      if (stash.length) { queue.push.apply(queue, stash); stash = []; }
      at.level += 1; at.page = 0; at.total = null; at.photosOver = false;
      if (at.level >= LEVELS.length) { done = true; return Promise.resolve(); }
    }
    var level = at.level, page = at.page + 1;
    return getJson(level, page).then(function (j) {
      at.page = page;
      at.total = Number(j.total_count) || 0;
      var t = document.createElement('template');
      t.innerHTML = j.html || '';
      Array.prototype.forEach.call(t.content.querySelectorAll('.jdgm-rev'), function (rev) {
        var id = rev.getAttribute('data-review-id');
        if (!id || seen[id]) return;
        seen[id] = 1;
        var photo = hasPhoto(rev), mine = kept(rev);
        // photos come first inside a level, so the first card without one ends that level's photo reviews
        if (!photo && !at.photosOver) { at.photosOver = true; if (stash.length) { queue.push.apply(queue, stash); stash = []; } }
        if (mine) queue.push(rev);
        else if (EXTRAS && photo && LEVELS[level] === 5) { rev.setAttribute('data-jmr-extra', ''); stash.push(rev); }
      });
      if (!(j.html || '').trim()) at.total = 0;
    });
  }

  // Resolve with up to n cards, fetching as many Judge.me pages as needed.
  function take(n) {
    if (queue.length >= n || done) return Promise.resolve(queue.splice(0, n));
    return pull().then(function () { return take(n); });
  }

  function decorate(rev) {
    if (C.showProduct === false || rev.querySelector('.jmr__product')) return;
    var name = productName(rev);
    if (!name) return;
    var el = document.createElement('div');
    el.className = 'jmr__product';
    el.textContent = rev.hasAttribute('data-jmr-extra') && C.extraPrefix ? C.extraPrefix + ' ' + name : name;
    var hd = rev.querySelector('.jdgm-rev__header');
    if (hd && hd.parentNode) hd.parentNode.insertBefore(el, hd.nextSibling); else rev.insertBefore(el, rev.firstChild);
  }

  function add(revs, replace) {
    if (replace) list.innerHTML = '';
    revs.forEach(function (r) { list.appendChild(r); });
    try {
      // Judge.me sets up every review not yet set up: badges, read more, votes, dates ...
      if (window.jdgm && jdgm.customizeReviews) jdgm.customizeReviews();
      // ... and the photo popup (without this a photo opens in a new tab); the name differs between Judge.me builds
      var pics = window.jdgm && (jdgm.setupPictures || jdgm.setupMediaGallery);
      if (pics && jdgm.$) pics.call(jdgm, jdgm.$(revs));
    } catch (e) { if (window.console) console.warn('[jm-ordered-reviews] setup', e); }
    revs.forEach(decorate);
    btn.hidden = done && !queue.length;
  }

  function load(replace) {
    if (busy) return Promise.resolve();
    busy = true;
    btn.disabled = true; btn.textContent = 'Loading…';
    return take(STEP).then(function (revs) { add(revs, replace); })
      .catch(function (e) { if (window.console) console.warn('[jm-ordered-reviews]', e); if (replace) throw e; })
      .then(function () { busy = false; btn.disabled = false; btn.textContent = LABEL; });
  }

  function start() {
    widget = mount.querySelector('.jdgm-widget');
    list = widget && widget.querySelector('.jdgm-rev-widg__reviews');
    // wait until Judge.me has set its widget up, so our list replaces Judge.me's and not the other way round
    if (!list || !widget.querySelector('.jdgm--done-setup')) return false;
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'jmr__more';
    btn.textContent = LABEL;
    btn.addEventListener('click', function () { load(false); });
    list.parentNode.insertBefore(btn, list.nextSibling);
    load(true).then(function () { mount.classList.add('jmr-on'); }, function () { btn.remove(); });
    return true;
  }
  if (!start()) {
    var tries = 0;
    var t = setInterval(function () { if (start() || ++tries > 60) clearInterval(t); }, 250);
  }
})();
